import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  CloudDownload,
  Database,
  Download,
  Download as DownloadIcon,
  FileText,
  Folder,
  FolderOpen,
  Loader,
  Loader2,
  LogIn,
  LogOut,
  Moon,
  Shield,
  ShieldCheck,
  Sun,
  Trash2,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useTheme } from "./hooks/useTheme";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { AsyncJobQueue } from "./components/AsyncJobQueue";
import { LogEntry, LogViewer } from "./components/LogViewer";
import { getAsyncJobDisplayMessage, inferAsyncLogToneFromText, isNorthflankLogAccessWarning } from "./lib/asyncJobs";
import { api } from "./lib/api";
import type {
  AsyncJob,
  AsyncJobLogsResponse,
  AsyncJobsResponse,
  AsyncRemoteState,
  AsyncJobsStreamSnapshot,
  AuthUser,
  DownloadRequestBody,
  EnvCreateResponse,
  EnvFileInfo,
  EnvFilesResponse,
  ExistingEnvFileInfo,
  ImportHistoryRequestBody,
  LegacySyncTriggerRequestBody,
  LegacySyncTriggerResponse,
  RunPreviewCommand,
  RunPreviewResponse,
  UserResponse,
  UsersListResponse,
} from "./types/api";

type Mode = "all" | "emesse" | "ricevute" | "corrispettivi" | "all_plus_corrispettivi";

interface FormState {
  dal: string;
  al: string;
  mode: Mode;
  clients: string;
  incaricante: string;
  manualCf: string;
  manualPassword: string;
  manualPin: string;
  manualProfilo: "incDiretto" | "delegaDiretta" | "incDelega" | string;
  manualCfPivaStudio: string;
  selectedEnvFile: string;
  envCompany: string;
  envTenant: string;
  envSector: string;
  envInfo: string;
  envLlmRules: string;
  envTaxonomyDbs: string;
  importTenantCode: string;
  importRemoteClientFolder: string;
  importSourcePreset: string;
  importDirection: "emesse" | "ricevute";
  importRemotePath: string;
  importRemoteMetadataPath: string;
}

interface Toast {
  id: number;
  message: string;
  tone: "info" | "success" | "error" | "warning";
}

interface FileEntry {
  name: string;
  path: string;
  is_dir: boolean;
  size: number;
  mtime: number;
  children?: FileEntry[];
  childrenLoaded?: boolean;
}

interface DbTable {
  name: string;
  rows: number | null;
}

type ManagedUser = AuthUser & {
  created_at?: string | null;
  updated_at?: string | null;
  last_login_at?: string | null;
};

const INITIAL_FORM: FormState = {
  dal: "",
  al: "",
  mode: "all",
  clients: "",
  incaricante: "",
  manualCf: "",
  manualPassword: "",
  manualPin: "",
  manualProfilo: "incDiretto",
  manualCfPivaStudio: "",
  selectedEnvFile: "",
  envCompany: "",
  envTenant: "",
  envSector: "",
  envInfo: "",
  envLlmRules: "",
  envTaxonomyDbs: "",
  importTenantCode: "",
  importRemoteClientFolder: "",
  importSourcePreset: "ALL",
  importDirection: "ricevute",
  importRemotePath: "",
  importRemoteMetadataPath: "",
};

const HISTORICAL_IMPORT_SOURCES = [
  {
    id: "ALL",
    label: "Tutto",
    direction: "ricevute" as const,
    remoteSubfolder: "",
    remoteMetadataSubfolder: "",
  },
  {
    id: "FE_ACQ",
    label: "FE_ACQ",
    direction: "ricevute" as const,
    remoteSubfolder: "FE_ACQ",
    remoteMetadataSubfolder: "FE_ACQ_METADATO",
  },
  {
    id: "FE_ACQ_STORICO",
    label: "FE_ACQ_STORICO",
    direction: "ricevute" as const,
    remoteSubfolder: "FE_ACQ_STORICO",
    remoteMetadataSubfolder: "FE_ACQ_METADATO",
  },
  {
    id: "FE_VEN",
    label: "FE_VEN",
    direction: "emesse" as const,
    remoteSubfolder: "FE_VEN",
    remoteMetadataSubfolder: "FE_VEN_METADATO",
  },
  {
    id: "FE_VEN_STORICO",
    label: "FE_VEN_STORICO",
    direction: "emesse" as const,
    remoteSubfolder: "FE_VEN_STORICO",
    remoteMetadataSubfolder: "FE_VEN_METADATO",
  },
];

type SelectableEnvFileInfo = EnvFileInfo | ExistingEnvFileInfo;

const TENANT_CODE_MIN_LENGTH = 8;
const TENANT_CODE_MAX_LENGTH = 16;
const TENANT_CODE_REGEX = /^[A-Z0-9]{8,16}$/;

const normalizeProfilo = (value: string): "incDiretto" | "delegaDiretta" | "incDelega" => {
  if (!value) return "incDiretto";
  const trimmed = value.trim();
  if (trimmed === "1" || trimmed.toLowerCase() === "delegadiretta") return "delegaDiretta";
  if (trimmed === "3" || trimmed.toLowerCase() === "incdelega") return "incDelega";
  if (trimmed === "2" || trimmed.toLowerCase() === "incdiretto") return "incDiretto";
  if (trimmed === "delegaDiretta" || trimmed === "incDelega" || trimmed === "incDiretto") {
    return trimmed as "incDiretto" | "delegaDiretta" | "incDelega";
  }
  return "incDiretto";
};

const sanitizeTenantCodeInput = (value: string) => value.replace(/\s+/g, "").toUpperCase().slice(0, TENANT_CODE_MAX_LENGTH);

const sanitizeTenantCode = (value: string) => value.replace(/\s+/g, "").toUpperCase();

const sanitizeTenantCodeMultiline = (value: string) =>
  value
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => sanitizeTenantCode(line))
    .join("\n");

const parseTenantCodeList = (value: string) =>
  value
    .replace(/\r/g, "")
    .split("\n")
    .map((entry) => entry.trim())
    .filter(Boolean);

const isValidTenantCode = (value: string) => TENANT_CODE_REGEX.test(value.trim().toUpperCase());

const cleanTqdmMessage = (raw: string) => {
  // Tqdm usa \\r per riscrivere la stessa riga: conserva solo l'ultimo pezzo
  const last = raw.split("\r").pop() || raw;
  return last.replace(/\s+\|\s+/g, " | ").trim();
};

const isProgressMessage = (msg: string) => msg.includes("%") && msg.includes("|") && /\d{1,3}%\|/.test(msg);

const progressPrefix = (msg: string) => msg.replace(/\s+\d{1,3}%.*$/, "").trim();
const progressPercent = (msg: string) => {
  const match = msg.match(/(\d{1,3})%/);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  if (Number.isNaN(n)) return null;
  return Math.max(0, Math.min(100, n));
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "Mai";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("it-IT", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const formatTimeOnly = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const extractApiErrorDetail = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  return err?.message || fallback;
};

const secondsUntil = (value?: string | null, now = Date.now()) => {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - now) / 1000));
};

const formatCountdown = (seconds: number | null) => {
  if (seconds === null) return "";
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder.toString().padStart(2, "0")}s` : `${remainder}s`;
};

const ASYNC_STAGE_LABELS: Record<string, string> = {
  download: "Download ADE",
  "historical-import": "Import storico SSH",
  "legacy-sync": "Sync vecchio PC",
  "xml-processing": "XML processing",
  bronzing: "Bronzing",
  dbt: "DBT",
  golding: "Golding",
};

const formatAsyncJobLogLine = (ts: string | null | undefined, text: string) => {
  if (!ts) return text;
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return `${ts} | ${text}`;
  return `${date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} | ${text}`;
};

const ASYNC_JOB_LOGS_POLL_INTERVAL_MS = 12000;
const ASYNC_JOBS_STREAM_RECONNECT_DELAY_MS = 3000;

const asyncJobTenantLabel = (job: AsyncJob) => job.tenant_codes?.[0] || job.tenant || job.company || "n/d";

const asyncJobToastMessage = (job: AsyncJob) => {
  const label = ASYNC_STAGE_LABELS[job.stage] || job.stage;
  const tenant = asyncJobTenantLabel(job);
  if (job.status === "success") return `${label} Northflank OK · tenant ${tenant}`;
  if (job.status === "failed") return `${label} Northflank KO · tenant ${tenant}`;
  return `${label} Northflank avviato · tenant ${tenant}`;
};

const ThemeToggle = React.memo(() => {
  const [theme, setTheme] = useTheme();
  const isDark = theme === "dark";
  const icon = isDark ? <Sun size={18} /> : <Moon size={18} />;
  const label = isDark ? "Tema chiaro" : "Tema scuro";
  return (
    <button
      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-100 bg-white/5 hover:bg-white/10 transition"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline text-sm">{label}</span>
    </button>
  );
});
ThemeToggle.displayName = "ThemeToggle";

interface FileTreeNodeProps {
  entry: FileEntry;
  depth?: number;
  busy: boolean;
  expandedPaths: Record<string, boolean>;
  onToggleDir: (path: string) => void;
  onOpenDir: (entry: FileEntry) => void;
  onDownload: (entry: FileEntry) => void;
  onDelete: (entry: FileEntry) => void;
}

const FileTreeNode = React.memo(({
  entry,
  depth = 0,
  busy,
  expandedPaths,
  onToggleDir,
  onOpenDir,
  onDownload,
  onDelete,
}: FileTreeNodeProps) => {
  const hasChildren = !!entry.children?.length;
  const isExpanded = entry.is_dir ? expandedPaths[entry.path] === true : false;

  return (
    <div className="relative">
      <div
        className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition ${
          depth === 0 ? "bg-white/[0.03]" : "hover:bg-white/[0.04]"
        }`}
        style={{ paddingLeft: `${depth * 18 + 8}px` }}
        onDoubleClick={() => (entry.is_dir ? onOpenDir(entry) : onDownload(entry))}
      >
        <div className="absolute inset-y-0 left-0 flex pointer-events-none">
          {depth > 0 && (
            <div
              className="border-l border-white/10"
              style={{ marginLeft: `${depth * 18 + 2}px` }}
            />
          )}
        </div>

        <button
          type="button"
          onClick={() => entry.is_dir && onToggleDir(entry.path)}
          className={`flex h-5 w-5 items-center justify-center rounded text-slate-400 ${
            entry.is_dir ? "hover:bg-white/10 hover:text-slate-100" : "cursor-default opacity-30"
          }`}
          disabled={!entry.is_dir}
          title={entry.is_dir ? "Espandi/comprimi" : ""}
        >
          {entry.is_dir ? (
            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : (
            <span className="block h-px w-2 bg-white/20" />
          )}
        </button>

        {entry.is_dir ? (
          isExpanded ? (
            <FolderOpen className="text-amber-300 shrink-0" size={16} />
          ) : (
            <Folder className="text-amber-300 shrink-0" size={16} />
          )
        ) : (
          <FileText className="text-brand-300 shrink-0" size={16} />
        )}

        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-slate-100">{entry.name}</div>
        </div>

        <div className="shrink-0 text-[11px] text-slate-500">
          {entry.is_dir ? `${entry.childrenLoaded ? entry.children?.length ?? 0 : "..."}` : `${Math.max(1, Math.round(entry.size / 1024))} KB`}
        </div>

        <button
          type="button"
          onClick={() => onDownload(entry)}
          className="p-1.5 text-slate-500 hover:text-emerald-400 hover:bg-emerald-400/10 rounded-md transition-colors"
          title={entry.is_dir ? "Scarica cartella" : "Scarica file"}
          disabled={busy}
        >
          <CloudDownload size={15} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(entry)}
          className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-400/10 rounded-md transition-colors"
          title="Elimina"
          disabled={busy}
        >
          <Trash2 size={15} />
        </button>
      </div>

      {entry.is_dir && isExpanded && hasChildren && (
        <div className="ml-2 border-l border-white/10">
          {entry.children!.map((child) => (
            <FileTreeNode
              key={child.path}
              entry={child}
              depth={depth + 1}
              busy={busy}
              expandedPaths={expandedPaths}
              onToggleDir={onToggleDir}
              onOpenDir={onOpenDir}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
});
FileTreeNode.displayName = "FileTreeNode";

interface CredentialsPanelProps {
  form: FormState;
  updateField: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}

const CredentialsPanel = React.memo(({ form, updateField }: CredentialsPanelProps) => (
  <div className="glass rounded-xl border border-white/10 p-4 space-y-4">
    <div className="text-sm font-semibold text-slate-100">Accesso e finestra temporale</div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Credenziali ADE</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="label">CF utente ADE</label>
            <input
              className="input mt-1 text-sm h-[44px]"
              value={form.manualCf}
              onChange={(e) => updateField("manualCf", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Password ADE</label>
            <input
              className="input mt-1 text-sm h-[44px]"
              value={form.manualPassword}
              onChange={(e) => updateField("manualPassword", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">PIN ADE</label>
            <input
              className="input mt-1 text-sm h-[44px]"
              value={form.manualPin}
              onChange={(e) => updateField("manualPin", e.target.value)}
              required
            />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Intervallo e tipo</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label whitespace-nowrap">Data inizio</label>
            <input
              type="date"
              className="input mt-1 text-sm h-[44px]"
              value={form.dal}
              onChange={(e) => updateField("dal", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label whitespace-nowrap">Data fine (opzionale)</label>
            <input
              type="date"
              className="input mt-1 text-sm h-[44px]"
              value={form.al}
              onChange={(e) => updateField("al", e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Tipo download</label>
            <select
              className="input mt-1 text-sm h-[44px]"
              value={form.mode}
              onChange={(e) => updateField("mode", e.target.value as Mode)}
            >
              <option value="all">Tutte (Emesse + Ricevute + Corrispettivi)</option>
              <option value="emesse">Solo Emesse</option>
              <option value="ricevute">Solo Ricevute</option>
              <option value="corrispettivi">Solo Corrispettivi</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
));
CredentialsPanel.displayName = "CredentialsPanel";

const ProfiloPanel = React.memo(({ normalizedProfilo, updateField }: { normalizedProfilo: string; updateField: any }) => (
  <div className="glass rounded-xl border border-white/10 p-4 space-y-3">
    <div className="text-sm font-semibold text-slate-100">Utenza di lavoro</div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {[
        {
          id: "incDiretto",
          title: "Incarico diretto",
          desc: "Hai nominato il professionista in modo diretto come tuo rappresentante presso l’Agenzia delle Entrate.",
          icon: <UserCheck size={18} className="text-emerald-300" />,
        },
        {
          id: "incDelega",
          title: "Incarico per delega",
          desc: "Il professionista lavora solo per conto tuo tramite deleghe digitali.",
          icon: <Users size={18} className="text-sky-300" />,
        },
        {
          id: "delegaDiretta",
          title: "Delega diretta",
          desc: "Delega digitale unica che abilita il professionista a operare online su più servizi.",
          icon: <Shield size={18} className="text-amber-300" />,
        },
      ].map((card) => (
        <button
          type="button"
          key={card.id}
          onClick={() => updateField("manualProfilo", card.id)}
          className={`w-full text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition ${normalizedProfilo === card.id
            ? "border-brand-400 bg-white/10"
            : "border-white/10 bg-white/5 hover:border-white/20"
            }`}
        >
          <div className="mt-0.5">{card.icon}</div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-100">{card.title}</div>
            <div className="text-xs text-slate-400">{card.desc}</div>
          </div>
          {normalizedProfilo === card.id && <Check size={16} className="text-brand-300" />}
        </button>
      ))}
    </div>
  </div>
));
ProfiloPanel.displayName = "ProfiloPanel";

const ClientsPanel = React.memo(
  ({
    clients,
    incaricante,
    studioCfPiva,
    updateField,
    required,
    showStudio,
    showIncaricante,
    incaricanteRequired,
  }: {
    clients: string;
    incaricante: string;
    studioCfPiva: string;
    updateField: any;
    required: boolean;
    showStudio: boolean;
    showIncaricante: boolean;
    incaricanteRequired: boolean;
  }) => (
    <div className="glass rounded-xl border border-white/10 p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-100">CF/P.IVA tenant</div>
      </div>
      <textarea
        className="input mt-1 min-h-[120px] font-mono"
        placeholder="04928410234&#10;DNZVNC76L71B296U"
        value={clients}
        onChange={(e) => updateField("clients", sanitizeTenantCodeMultiline(e.target.value))}
        required={required}
      />
      {showIncaricante && (
        <div>
          <label className="label">Incaricante</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            placeholder="Es. 04282760232 o 04282760232-000"
            value={incaricante}
            onChange={(e) => updateField("incaricante", e.target.value)}
            required={incaricanteRequired}
          />
        </div>
      )}
      {showStudio && (
        <div>
          <label className="label">CF o P.IVA studio</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            value={studioCfPiva}
            onChange={(e) => updateField("manualCfPivaStudio", e.target.value)}
          />
        </div>
      )}
    </div>
));
ClientsPanel.displayName = "ClientsPanel";

const TenantEnvPanel = React.memo(
  ({
    form,
    updateField,
    existingEnvFiles,
    onSelectExistingEnv,
  }: {
    form: FormState;
    updateField: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
    existingEnvFiles: ExistingEnvFileInfo[];
    onSelectExistingEnv: (envFilePath: string) => void;
  }) => (
    <div className="glass rounded-xl border border-white/10 p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-100">Env file tenant</div>
      </div>
      <div>
        <label className="label">Usa env esistente</label>
        <select
          className="input mt-1 text-sm h-[44px]"
          value={form.selectedEnvFile || ""}
          onChange={(e) => onSelectExistingEnv(e.target.value)}
        >
          <option value="">Crea o aggiorna dai campi sotto</option>
          {existingEnvFiles.map((item) => (
            <option key={item.env_file} value={item.env_file}>
              {item.company || item.tenant || item.name} · {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label className="label">Company</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            value={form.envCompany || ""}
            onChange={(e) => updateField("envCompany", e.target.value)}
            placeholder="04928410234"
          />
        </div>
        <div>
          <label className="label">Tenant</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            value={form.envTenant || ""}
            onChange={(e) => updateField("envTenant", sanitizeTenantCodeInput(e.target.value))}
            placeholder="04330980246 o SRNGCM96A03I775W"
            maxLength={TENANT_CODE_MAX_LENGTH}
          />
        </div>
        <div>
          <label className="label">Sector</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            value={form.envSector || ""}
            onChange={(e) => updateField("envSector", e.target.value)}
            placeholder="horeca"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="label">Info cliente</label>
          <textarea
            className="input input-scroll mt-1 h-[132px] resize-none overflow-y-auto leading-6"
            value={form.envInfo || ""}
            onChange={(e) => updateField("envInfo", e.target.value)}
            placeholder="Descrizione del cliente e del settore..."
          />
        </div>
        <div>
          <label className="label">LLM Rules</label>
          <textarea
            className="input input-scroll mt-1 h-[118px] resize-none overflow-y-auto font-mono leading-6"
            value={form.envLlmRules || ""}
            onChange={(e) => updateField("envLlmRules", e.target.value)}
            placeholder=""
          />
        </div>
        <div>
          <label className="label">Database Tassonomia</label>
          <textarea
            className="input input-scroll mt-1 h-[118px] resize-none overflow-y-auto font-mono leading-6"
            value={form.envTaxonomyDbs || ""}
            onChange={(e) => updateField("envTaxonomyDbs", e.target.value)}
            placeholder=""
          />
        </div>
      </div>
    </div>
  ),
);
TenantEnvPanel.displayName = "TenantEnvPanel";

const HistoricalImportPanel = React.memo(
  ({
    form,
    updateField,
    tenantOptions,
    onStart,
    onTriggerLegacySync,
    onApplyPreset,
    disabled,
    legacySyncDisabled,
    legacySyncHint,
  }: {
    form: FormState;
    updateField: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
    tenantOptions: string[];
    onStart: () => void;
    onTriggerLegacySync: () => void;
    onApplyPreset: (presetId: string, clientFolder: string) => void;
    disabled: boolean;
    legacySyncDisabled: boolean;
    legacySyncHint?: string | null;
  }) => (
    <div className="glass rounded-xl border border-white/10 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-100">Import storico fatture</div>
          <div className="text-xs text-slate-400 mt-1">
            Copia via SSH i file storici nel layer metal e poi lancia bronze, DBT e gold usando l&apos;env selezionato.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-ghost" onClick={onTriggerLegacySync} disabled={legacySyncDisabled}>
            <Folder size={16} />
            Sync vecchio PC
          </button>
          <button type="button" className="btn-ghost" onClick={onStart} disabled={disabled}>
            <DownloadIcon size={16} />
            Avvia import
          </button>
        </div>
      </div>
      {legacySyncHint && (
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          {legacySyncHint}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Cartella cliente remota</label>
          <input
            className="input mt-1 text-sm h-[44px]"
            value={form.importRemoteClientFolder}
            onChange={(e) => {
              updateField("importRemoteClientFolder", e.target.value);
              if (form.importSourcePreset) onApplyPreset(form.importSourcePreset, e.target.value);
            }}
            placeholder="danziveronica"
          />
        </div>
        <div>
          <label className="label">Sorgente dati</label>
          <select
            className="input mt-1 text-sm h-[44px]"
            value={form.importSourcePreset}
            onChange={(e) => {
              updateField("importSourcePreset", e.target.value);
              onApplyPreset(e.target.value, form.importRemoteClientFolder);
            }}
          >
            <option value="">Manuale</option>
            {HISTORICAL_IMPORT_SOURCES.map((source) => (
              <option key={source.id} value={source.id}>
                {source.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Tenant target</label>
          <select
            className="input mt-1 text-sm h-[44px]"
            value={form.importTenantCode}
            onChange={(e) => updateField("importTenantCode", e.target.value)}
          >
            <option value="">Seleziona tenant</option>
            {tenantOptions.map((tenant) => (
              <option key={tenant} value={tenant}>
                {tenant}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Tipo fatture</label>
          <select
            className="input mt-1 text-sm h-[44px]"
            value={form.importDirection}
            onChange={(e) => updateField("importDirection", e.target.value as "emesse" | "ricevute")}
            disabled={form.importSourcePreset === "ALL"}
          >
            <option value="ricevute">Ricevute</option>
            <option value="emesse">Emesse</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label">Percorso remoto fatture</label>
        <input
          className="input mt-1 text-sm h-[44px] font-mono"
          value={form.importRemotePath}
          onChange={(e) => updateField("importRemotePath", e.target.value)}
          placeholder="DANZI_VERONICA/FattureStoriche/Ricevute"
        />
      </div>
      <div>
        <label className="label">Percorso remoto metadati (opzionale)</label>
        <input
          className="input mt-1 text-sm h-[44px] font-mono"
          value={form.importRemoteMetadataPath}
          onChange={(e) => updateField("importRemoteMetadataPath", e.target.value)}
          placeholder="DANZI_VERONICA/FattureStoriche/Metadati"
        />
      </div>
    </div>
  ),
);
HistoricalImportPanel.displayName = "HistoricalImportPanel";

ThemeToggle.displayName = "ThemeToggle";

function UsersAdminPanel({ pushToast }: { pushToast: (t: Toast) => void }) {
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { username: string; role: string; is_active: boolean; password: string }>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "user",
    is_active: true,
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<UsersListResponse>("/users");
      const nextUsers = res.data.users || [];
      setUsers(nextUsers);
      setDrafts(
        Object.fromEntries(
          nextUsers.map((item) => [
            item.id,
            {
              username: item.username,
              role: item.role,
              is_active: item.is_active !== false,
              password: "",
            },
          ]),
        ),
      );
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Impossibile caricare gli utenti", tone: "error" });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const updateDraft = (userId: string, key: "username" | "role" | "is_active" | "password", value: string | boolean) => {
    setDrafts((prev) => ({
      ...prev,
      [userId]: {
        username: prev[userId]?.username || "",
        role: prev[userId]?.role || "user",
        is_active: prev[userId]?.is_active ?? true,
        password: prev[userId]?.password || "",
        [key]: value,
      },
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post<UserResponse>("/users", newUser);
      pushToast({ id: Date.now(), message: `Utente ${res.data.user.username} creato`, tone: "success" });
      setNewUser({ username: "", password: "", role: "user", is_active: true });
      await loadUsers();
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Creazione utente fallita", tone: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (userId: string) => {
    const draft = drafts[userId];
    if (!draft) return;
    setSavingUserId(userId);
    try {
      const payload: Record<string, string | boolean> = {
        username: draft.username.trim(),
        role: draft.role,
        is_active: draft.is_active,
      };
      if (draft.password.trim()) payload.password = draft.password;
      const res = await api.put<UserResponse>(`/users/${userId}`, payload);
      pushToast({ id: Date.now(), message: `Utente ${res.data.user.username} aggiornato`, tone: "success" });
      await loadUsers();
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Aggiornamento utente fallito", tone: "error" });
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[360px,minmax(0,1fr)]">
      <div className="glass rounded-xl border border-white/10 p-4 space-y-4 self-start">
        <div>
          <div className="text-sm font-semibold text-slate-100">Crea utente</div>
          <div className="text-xs text-slate-400 mt-1">
            Schema separato `myade_auth`, accesso completo per tutti gli utenti per ora.
          </div>
        </div>
        <form className="space-y-3" onSubmit={handleCreate}>
          <div>
            <label className="label">Username</label>
            <input
              className="input mt-1 text-sm h-[44px]"
              value={newUser.username}
              onChange={(e) => setNewUser((prev) => ({ ...prev, username: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              className="input mt-1 text-sm h-[44px]"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Ruolo</label>
              <select
                className="input mt-1 text-sm h-[44px]"
                value={newUser.role}
                onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <label className="flex items-end gap-2 text-sm text-slate-300 pb-2">
              <input
                type="checkbox"
                checked={newUser.is_active}
                onChange={(e) => setNewUser((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
              Attivo
            </label>
          </div>
          <button type="submit" className="btn-primary w-full" disabled={creating}>
            {creating ? <Loader2 className="animate-spin" size={16} /> : <Users size={16} />}
            {creating ? "Creazione..." : "Crea utente"}
          </button>
        </form>
      </div>

      <div className="glass rounded-xl border border-white/10 p-4 space-y-4 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">Utenti applicativi</div>
            <div className="text-xs text-slate-400 mt-1">
              Connesso come <span className="text-slate-200">{user?.username}</span>.
            </div>
          </div>
          <button type="button" className="btn-ghost" onClick={() => void loadUsers()} disabled={loading}>
            {loading ? <Loader2 className="animate-spin" size={14} /> : <Users size={14} />}
            Aggiorna
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-300 text-sm">
            <Loader className="animate-spin" size={16} /> Caricamento utenti...
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((managedUser) => {
              const draft = drafts[managedUser.id];
              return (
                <div key={managedUser.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr),180px,120px,150px]">
                    <div>
                      <label className="label">Username</label>
                      <input
                        className="input mt-1 text-sm h-[42px]"
                        value={draft?.username || ""}
                        onChange={(e) => updateDraft(managedUser.id, "username", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="label">Ruolo</label>
                      <select
                        className="input mt-1 text-sm h-[42px]"
                        value={draft?.role || "user"}
                        onChange={(e) => updateDraft(managedUser.id, "role", e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <label className="flex items-end gap-2 text-sm text-slate-300 pb-2">
                      <input
                        type="checkbox"
                        checked={draft?.is_active ?? true}
                        onChange={(e) => updateDraft(managedUser.id, "is_active", e.target.checked)}
                      />
                      Attivo
                    </label>
                    <div>
                      <label className="label">Nuova password</label>
                      <input
                        className="input mt-1 text-sm h-[42px]"
                        type="password"
                        value={draft?.password || ""}
                        onChange={(e) => updateDraft(managedUser.id, "password", e.target.value)}
                        placeholder="Lascia vuoto per non cambiare"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                    <div className="flex flex-wrap items-center gap-4">
                      <span>Creato: {formatDateTime(managedUser.created_at)}</span>
                      <span>Ultimo login: {formatDateTime(managedUser.last_login_at)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => void handleSave(managedUser.id)}
                      disabled={savingUserId === managedUser.id}
                    >
                      {savingUserId === managedUser.id ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />}
                      Salva
                    </button>
                  </div>
                </div>
              );
            })}
            {users.length === 0 && (
              <div className="text-sm text-slate-400">Nessun utente configurato.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LoginView({ pushToast }: { pushToast: (t: Toast) => void }) {
  const { login, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      pushToast({ id: Date.now(), message: `Benvenuto, ${username}!`, tone: "success" });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Login fallito";
      pushToast({ id: Date.now(), message: `❌ ${detail}`, tone: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-2xl bg-brand-500/20 border border-brand-400/50 flex items-center justify-center text-brand-200">
            <ShieldCheck size={22} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">ADE Control Room</p>
            <h1 className="text-xl font-semibold">Accedi</h1>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="label">Username</label>
            <input
              className="input mt-1"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              className="input mt-1"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || loading}
            className="btn-primary w-full"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
            {submitting ? "Accesso in corso..." : "Entra"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ pushToast }: { pushToast: (t: Toast) => void }) {
  const { user, logout, token, authEnabled } = useAuth();
  const [form, setForm] = useLocalStorage<FormState>("ade_form_state", INITIAL_FORM);
  const [logs, setLogs] = useState<LogEntry[]>([
    { text: "In attesa di avviare il download...", tone: "muted" },
  ]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"idle" | "running" | "success" | "error">("idle");
  const downloadControllerRef = useRef<AbortController | null>(null);
  const downloadFormRef = useRef<HTMLFormElement | null>(null);
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileBase, setFileBase] = useState<string>("");
  const [fileLoading, setFileLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>("");
  const [expandedTreePaths, setExpandedTreePaths] = useLocalStorage<Record<string, boolean>>("volume_tree_expanded_paths", {});
  const [dbTables, setDbTables] = useState<DbTable[]>([]);
  const [dbTableRows, setDbTableRows] = useState<(string | number | null)[][]>([]);
  const [dbColumns, setDbColumns] = useState<string[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [dbModalOpen, setDbModalOpen] = useState(false);
  const [dbSelected, setDbSelected] = useState<string>("");
  const [dbPath, setDbPath] = useState<string>("");
  const [dbDownloadLoading, setDbDownloadLoading] = useState(false);
  const [liveProgress, setLiveProgress] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<"downloader" | "volume" | "users">("downloader");
  const [asyncJobs, setAsyncJobs] = useState<AsyncJob[]>([]);
  const [asyncJobsLoading, setAsyncJobsLoading] = useState(false);
  const [asyncJobsRefreshedAt, setAsyncJobsRefreshedAt] = useState<string | null>(null);
  const [asyncJobsRemoteState, setAsyncJobsRemoteState] = useState<AsyncRemoteState>("ok");
  const [asyncJobsRemoteRetryAt, setAsyncJobsRemoteRetryAt] = useState<string | null>(null);
  const [asyncJobsRemoteMessage, setAsyncJobsRemoteMessage] = useState<string | null>(null);
  const [selectedAsyncJob, setSelectedAsyncJob] = useState<AsyncJob | null>(null);
  const [asyncJobLogs, setAsyncJobLogs] = useState<LogEntry[]>([]);
  const [asyncJobLogsLive, setAsyncJobLogsLive] = useState(false);
  const [asyncJobLogsLoading, setAsyncJobLogsLoading] = useState(false);
  const [asyncJobModalOpen, setAsyncJobModalOpen] = useState(false);
  const [legacySyncRetryAt, setLegacySyncRetryAt] = useState<string | null>(null);
  const [clockNow, setClockNow] = useState(Date.now());
  const asyncJobStatusesRef = useRef<Map<string, AsyncJob["status"]>>(new Map());
  const asyncJobsStreamPrimedRef = useRef(false);
  const asyncJobsSessionInvalidRef = useRef(false);

  const liveProgressRef = useRef<string | null>(null);
  const setLiveProgressWithRef = useCallback((val: string | null) => {
    liveProgressRef.current = val;
    setLiveProgress(val);
  }, []);

  const handleSessionInvalid = useCallback(
    (source: string) => {
      if (!authEnabled || asyncJobsSessionInvalidRef.current) return;
      asyncJobsSessionInvalidRef.current = true;
      pushToast({
        id: Date.now(),
        message: `Sessione scaduta durante ${source}. Effettua di nuovo il login.`,
        tone: "warning",
      });
      logout();
    },
    [authEnabled, logout, pushToast],
  );

  useEffect(() => {
    if (token) {
      asyncJobsSessionInvalidRef.current = false;
    }
  }, [token]);

  useEffect(() => {
    const hasRetryCountdown = Boolean(
      legacySyncRetryAt
      || asyncJobsRemoteRetryAt
      || selectedAsyncJob?.remote_retry_at,
    );
    if (!hasRetryCountdown) return;
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [asyncJobsRemoteRetryAt, legacySyncRetryAt, selectedAsyncJob?.remote_retry_at]);

  const normalizedProfilo = useMemo(() => normalizeProfilo(form.manualProfilo), [form.manualProfilo]);
  const [commandPreview, setCommandPreview] = useState<RunPreviewCommand[]>([]);
  const [commandPreviewMissing, setCommandPreviewMissing] = useState<string[]>([]);
  const [commandPreviewLoading, setCommandPreviewLoading] = useState(false);
  const [existingEnvFiles, setExistingEnvFiles] = useState<ExistingEnvFileInfo[]>([]);
  const appUsername = (user?.username || "").trim();
  const isAdmin = (user?.role || "") === "admin";
  const hasRealAppUser = !!appUsername && appUsername.toLowerCase() !== "guest";
  const sessionLabel = hasRealAppUser ? appUsername : "Accesso libero";

  const reloadEnvFiles = useCallback(async (): Promise<ExistingEnvFileInfo[]> => {
    try {
      const res = await api.get<EnvFilesResponse>("/env/files");
      const envFiles = res.data.env_files || [];
      setExistingEnvFiles(envFiles);
      return envFiles;
    } catch {
      setExistingEnvFiles([]);
      return [];
    }
  }, []);

  const asyncJobsTenantFilter = useMemo(() => {
    const explicitTenant = (form.envTenant || "").trim();
    if (isValidTenantCode(explicitTenant)) return explicitTenant;

    const parsedClients = parseTenantCodeList(form.clients).filter((value) => isValidTenantCode(value));
    if (parsedClients.length === 1) return parsedClients[0];

    const selected = existingEnvFiles.find((item) => item.env_file === form.selectedEnvFile);
    const selectedTenant = (selected?.tenant || "").trim();
    if (isValidTenantCode(selectedTenant)) return selectedTenant;

    const tenantCodes = (selected?.tenant_codes || []).filter((value) => isValidTenantCode(value));
    if (tenantCodes.length === 1) return tenantCodes[0];

    return null;
  }, [existingEnvFiles, form.clients, form.envTenant, form.selectedEnvFile]);

  const effectiveLegacySyncRetryAt = useMemo(() => {
    const directRetry = secondsUntil(legacySyncRetryAt, clockNow);
    if (directRetry !== null && directRetry > 0) return legacySyncRetryAt;
    if (asyncJobsRemoteState === "rate_limited") return asyncJobsRemoteRetryAt;
    return null;
  }, [asyncJobsRemoteRetryAt, asyncJobsRemoteState, clockNow, legacySyncRetryAt]);

  const legacySyncRetrySeconds = useMemo(
    () => secondsUntil(effectiveLegacySyncRetryAt, clockNow),
    [clockNow, effectiveLegacySyncRetryAt],
  );

  const legacySyncDisabled = isRunning || (legacySyncRetrySeconds !== null && legacySyncRetrySeconds > 0);

  const legacySyncHint = useMemo(() => {
    if (legacySyncRetrySeconds !== null && legacySyncRetrySeconds > 0) {
      const retryTime = formatTimeOnly(effectiveLegacySyncRetryAt);
      return retryTime
        ? `Northflank rate-limited: riprova tra ${formatCountdown(legacySyncRetrySeconds)} circa, dopo le ${retryTime}.`
        : `Northflank rate-limited: riprova tra ${formatCountdown(legacySyncRetrySeconds)} circa.`;
    }
    if (asyncJobsRemoteState === "stale" && asyncJobsRemoteMessage) {
      return asyncJobsRemoteMessage;
    }
    return null;
  }, [asyncJobsRemoteMessage, asyncJobsRemoteState, clockNow, effectiveLegacySyncRetryAt, legacySyncRetrySeconds]);

  const selectedAsyncJobRetrySeconds = useMemo(
    () => secondsUntil(selectedAsyncJob?.remote_retry_at, clockNow),
    [clockNow, selectedAsyncJob?.remote_retry_at],
  );

  const selectedAsyncJobRemoteHint = useMemo(() => {
    if (!selectedAsyncJob || selectedAsyncJob.source !== "northflank") return null;
    if (selectedAsyncJob.remote_state === "rate_limited") {
      const retryTime = formatTimeOnly(selectedAsyncJob.remote_retry_at);
      if (selectedAsyncJobRetrySeconds !== null && selectedAsyncJobRetrySeconds > 0) {
        return retryTime
          ? `Northflank rate-limited: uso cache/log condivisi. Riprova tra ${formatCountdown(selectedAsyncJobRetrySeconds)}, dopo le ${retryTime}.`
          : `Northflank rate-limited: uso cache/log condivisi. Riprova tra ${formatCountdown(selectedAsyncJobRetrySeconds)}.`;
      }
      return selectedAsyncJob.remote_message || "Northflank rate-limited: uso cache/log condivisi.";
    }
    if (selectedAsyncJob.remote_state === "stale") {
      return selectedAsyncJob.remote_message || "Vista cached-first: log condivisi locali attivi, refresh remoto solo manuale.";
    }
    return null;
  }, [clockNow, selectedAsyncJob, selectedAsyncJobRetrySeconds]);

  const syncAsyncJobToasts = useCallback((jobs: AsyncJob[], announce: boolean) => {
    const previousStatuses = asyncJobStatusesRef.current;
    const nextStatuses = new Map<string, AsyncJob["status"]>();

    jobs
      .filter((job) => job.source === "northflank")
      .forEach((job, index) => {
        nextStatuses.set(job.id, job.status);
        if (!announce) return;

        const previousStatus = previousStatuses.get(job.id);
        const isNewJob = previousStatus === undefined;
        const statusChanged = previousStatus !== job.status;

        if (isNewJob && (job.status === "queued" || job.status === "running")) {
          pushToast({
            id: Date.now() + index,
            message: asyncJobToastMessage(job),
            tone: "info",
          });
          return;
        }

        if ((isNewJob || statusChanged) && job.status === "success") {
          pushToast({
            id: Date.now() + index,
            message: asyncJobToastMessage(job),
            tone: "success",
          });
          return;
        }

        if ((isNewJob || statusChanged) && job.status === "failed") {
          pushToast({
            id: Date.now() + index,
            message: asyncJobToastMessage(job),
            tone: "error",
          });
        }
      });

    asyncJobStatusesRef.current = nextStatuses;
  }, [pushToast]);

  const applyAsyncJobsSnapshot = useCallback((payload: AsyncJobsResponse | AsyncJobsStreamSnapshot, announce: boolean) => {
    const jobs = payload.jobs || [];
    syncAsyncJobToasts(jobs, announce);
    setAsyncJobs(jobs);
    setAsyncJobsRefreshedAt(payload.refreshed_at || null);
    setAsyncJobsRemoteState(payload.remote_state || "ok");
    setAsyncJobsRemoteRetryAt(payload.remote_retry_at || null);
    setAsyncJobsRemoteMessage(payload.remote_message || null);
    setSelectedAsyncJob((prev) => {
      if (!prev) return prev;
      const refreshed = jobs.find((job) => job.id === prev.id);
      return refreshed || prev;
    });
  }, [syncAsyncJobToasts]);

  const loadAsyncJobs = useCallback(async () => {
    if (authEnabled && !token) {
      setAsyncJobsLoading(false);
      return;
    }
    setAsyncJobsLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        limit: 40,
        active_only: false,
        local_only: true,
      };
      if (asyncJobsTenantFilter) {
        params.tenant = asyncJobsTenantFilter;
      }
      const res = await api.get<AsyncJobsResponse>("/async-jobs", {
        params,
      });
      applyAsyncJobsSnapshot(res.data, false);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        handleSessionInvalid("la coda job async");
        return;
      }
      // lascia l'ultimo snapshot valido
    } finally {
      setAsyncJobsLoading(false);
    }
  }, [applyAsyncJobsSnapshot, asyncJobsTenantFilter, authEnabled, handleSessionInvalid, token]);

  const fetchAsyncJobLogs = useCallback(async (
    job: AsyncJob,
    options?: { localOnly?: boolean; remoteRefresh?: boolean },
  ) => {
    if (authEnabled && !token) {
      setAsyncJobLogs([{ text: "Sessione non valida. Effettua di nuovo il login.", tone: "warning" }]);
      setAsyncJobLogsLive(false);
      return;
    }
    const localOnly = options?.localOnly ?? true;
    const remoteRefresh = options?.remoteRefresh ?? false;
    setAsyncJobLogsLoading(true);
    try {
      const res = await api.get<AsyncJobLogsResponse>(`/async-jobs/${job.id}/logs`, {
        params: {
          line_limit: 400,
          local_only: localOnly,
          remote_refresh: remoteRefresh,
        },
      });
      const nextJob = res.data.job || job;
      setSelectedAsyncJob(nextJob);
      setAsyncJobsRemoteState(res.data.remote_state || "ok");
      setAsyncJobsRemoteRetryAt(res.data.remote_retry_at || null);
      setAsyncJobsRemoteMessage(res.data.remote_message || null);
      setAsyncJobLogs(
        (res.data.lines || []).map((line) => ({
          text: formatAsyncJobLogLine(line.ts, line.text),
          tone: inferAsyncLogToneFromText(line.text),
        })),
      );
      setAsyncJobLogsLive(Boolean(res.data.live));
      setAsyncJobs((prev) => prev.map((item) => (item.id === nextJob.id ? nextJob : item)));
    } catch (err: any) {
      if (err?.response?.status === 401) {
        handleSessionInvalid("i log job async");
      }
      const message = extractApiErrorDetail(err, "Impossibile leggere i log del job");
      setAsyncJobLogs([{ text: message, tone: "error" }]);
      setAsyncJobLogsLive(false);
    } finally {
      setAsyncJobLogsLoading(false);
    }
  }, [authEnabled, handleSessionInvalid, token]);

  useEffect(() => {
    setForm((prev) => ({ ...INITIAL_FORM, ...prev }));
  }, [setForm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<EnvFilesResponse>("/env/files");
        if (!cancelled) setExistingEnvFiles(res.data.env_files || []);
      } catch {
        if (!cancelled) setExistingEnvFiles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (currentView !== "downloader") return;
    if (authEnabled && !token) {
      setAsyncJobsLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    let reconnectTimer: number | null = null;

    asyncJobsStreamPrimedRef.current = false;
    asyncJobStatusesRef.current = new Map();
    setAsyncJobsLoading(true);

    const connect = async () => {
      try {
        const base = (api.defaults.baseURL || "").replace(/\/$/, "");
        const params = new URLSearchParams({
          limit: "40",
          active_only: "false",
          local_only: "true",
        });
        if (asyncJobsTenantFilter) {
          params.set("tenant", asyncJobsTenantFilter);
        }

        const res = await fetch(`${base}/async-jobs/stream?${params.toString()}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          if (res.status === 401) {
            cancelled = true;
            handleSessionInvalid("lo stream della coda job");
            return;
          }
          const errText = await res.text();
          throw new Error(`HTTP ${res.status}: ${errText || "Errore stream job async"}`);
        }

        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error("Streaming job async non supportato dal browser.");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";

          for (const chunk of chunks) {
            if (!chunk.trim()) continue;
            const match = chunk.match(/^event: (.+)\ndata: (.+)$/s);
            if (!match) continue;
            const [, eventType, dataStr] = match;
            if (eventType !== "snapshot") continue;

            let payload: AsyncJobsStreamSnapshot;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }

            applyAsyncJobsSnapshot(payload, asyncJobsStreamPrimedRef.current);
            asyncJobsStreamPrimedRef.current = true;
            setAsyncJobsLoading(false);
          }
        }
      } catch (err: any) {
        if (cancelled || err?.name === "AbortError") return;
        void loadAsyncJobs();
      } finally {
        setAsyncJobsLoading(false);
      }

      if (cancelled) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, ASYNC_JOBS_STREAM_RECONNECT_DELAY_MS);
    };

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
    };
  }, [applyAsyncJobsSnapshot, asyncJobsTenantFilter, authEnabled, currentView, handleSessionInvalid, loadAsyncJobs, token]);

  useEffect(() => {
    if (!asyncJobModalOpen || !selectedAsyncJob) return;
    void fetchAsyncJobLogs(selectedAsyncJob);
    const timer = window.setInterval(() => {
      void fetchAsyncJobLogs(selectedAsyncJob);
    }, ASYNC_JOB_LOGS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [asyncJobModalOpen, selectedAsyncJob, fetchAsyncJobLogs]);

  // Gestione tasto ESC e tasto Indietro
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (dbModalOpen) setDbModalOpen(false);
        else if (isFileModalOpen) {
          if (currentPath) navigateUp();
          else setIsFileModalOpen(false);
        }
        else if (currentView !== "downloader") setCurrentView("downloader");
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      // Se c'è un modal aperto, lo chiudiamo invece di navigare via
      if (dbModalOpen) {
        setDbModalOpen(false);
        window.history.pushState(null, "");
      } else if (isFileModalOpen) {
        if (currentPath) {
          navigateUp();
          window.history.pushState(null, "");
        } else {
          setIsFileModalOpen(false);
          window.history.pushState(null, "");
        }
      } else if (currentView !== "downloader") {
        setCurrentView("downloader");
        window.history.pushState(null, "");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("popstate", handlePopState);

    // Inizializziamo lo stato della history per intercettare il tasto back
    window.history.pushState(null, "");

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dbModalOpen, isFileModalOpen, currentView, currentPath]);

  useEffect(() => {
    setStatus(isRunning ? "running" : "idle");
  }, [isRunning]);

  const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, [setForm]);

  const applyHistoricalImportPreset = useCallback((presetId: string, clientFolderRaw: string) => {
    const preset = HISTORICAL_IMPORT_SOURCES.find((item) => item.id === presetId);
    const clientFolder = clientFolderRaw.trim().replace(/^\/+|\/+$/g, "");
    if (!preset) {
      return;
    }
    if (preset.id === "ALL") {
      setForm((prev) => ({
        ...prev,
        importSourcePreset: preset.id,
        importDirection: "ricevute",
        importRemotePath: "",
        importRemoteMetadataPath: "",
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      importSourcePreset: preset.id,
      importDirection: preset.direction,
      importRemotePath: clientFolder ? `${clientFolder}/${preset.remoteSubfolder}` : preset.remoteSubfolder,
      importRemoteMetadataPath: clientFolder
        ? `${clientFolder}/${preset.remoteMetadataSubfolder}`
        : preset.remoteMetadataSubfolder,
    }));
  }, [setForm]);

  const applySelectedEnv = useCallback((selected: SelectableEnvFileInfo) => {
    const clientFolder = selected.company || "";
    setForm((prev) => {
      const activePreset = HISTORICAL_IMPORT_SOURCES.find((item) => item.id === prev.importSourcePreset);
      return {
        ...prev,
        selectedEnvFile: selected.env_file,
        dal: selected.ade_date_from || "",
        al: selected.ade_date_to || "",
        mode: (selected.ade_invoice_mode as Mode) || "all",
        clients: (selected.tenant_codes || []).length
          ? selected.tenant_codes.join("\n")
          : (selected.ade_target_cf_piva || selected.tenant || selected.company || ""),
        envCompany: selected.company || "",
        envTenant: selected.tenant || "",
        envSector: selected.sector || "",
        envInfo: selected.info || "",
        envLlmRules: selected.llm_rules || "",
        envTaxonomyDbs: selected.taxonomy_dbs || "",
        manualCf: selected.ade_cf || "",
        manualPassword: selected.ade_password || "",
        manualPin: selected.ade_pin || "",
        manualProfilo: normalizeProfilo(selected.ade_work_profile || ""),
        manualCfPivaStudio: selected.ade_studio_cf_piva || "",
        incaricante: selected.ade_incaricante || "",
        importTenantCode:
          (selected.tenant_codes || []).find(Boolean) ||
          selected.tenant ||
          "",
        importRemoteClientFolder: clientFolder,
        importRemotePath: activePreset
          ? (clientFolder ? `${clientFolder}/${activePreset.remoteSubfolder}` : activePreset.remoteSubfolder)
          : prev.importRemotePath,
        importRemoteMetadataPath: activePreset
          ? (
            clientFolder
              ? `${clientFolder}/${activePreset.remoteMetadataSubfolder}`
              : activePreset.remoteMetadataSubfolder
          )
          : prev.importRemoteMetadataPath,
      };
    });
  }, [setForm]);

  const handleSelectExistingEnv = useCallback((envFilePath: string) => {
    if (!envFilePath) {
      setForm((prev) => ({ ...prev, selectedEnvFile: "" }));
      return;
    }
    const selected = existingEnvFiles.find((item) => item.env_file === envFilePath);
    if (!selected) return;
    applySelectedEnv(selected);
  }, [applySelectedEnv, existingEnvFiles, setForm]);

  const logQueueRef = useRef<LogEntry[]>([]);

  useEffect(() => {
    if (!isRunning) {
      if (logQueueRef.current.length > 0) {
        setLogs(prev => [...prev, ...logQueueRef.current]);
        logQueueRef.current = [];
      }
      return;
    }
    const interval = setInterval(() => {
      if (logQueueRef.current.length > 0) {
        const batch = [...logQueueRef.current];
        logQueueRef.current = [];
        setLogs(prev => {
          let next = [...prev, ...batch];
          if (next.length > 1000) return next.slice(-1000);
          return next;
        });
      }
    }, 150);
    return () => clearInterval(interval);
  }, [isRunning]);

  const pushLog = useCallback((text: string, tone: LogEntry["tone"] = "info") => {
    const cleaned = cleanTqdmMessage(text);
    if (isProgressMessage(cleaned)) {
      const currentProgress = liveProgressRef.current;
      if (currentProgress && progressPrefix(currentProgress) !== progressPrefix(cleaned)) {
        logQueueRef.current.push({ text: currentProgress, tone: "muted" });
      }
      setLiveProgressWithRef(cleaned);
      const pct = progressPercent(cleaned);
      if (pct !== null) {
        setProgress((p) => Math.max(p, pct));
        setStatus("running");
      }
      return;
    }
    const lower = cleaned.toLowerCase();

    const isTransition =
      lower.includes("trasferimento ssh completato") ||
      lower.startsWith("-> upload verso r2") ||
      lower.includes("upload r2 terminato") ||
      lower.startsWith("upload r2");

    // Process transitions immediately for UI responsiveness
    if (liveProgressRef.current && isTransition) {
      logQueueRef.current.push({ text: liveProgressRef.current, tone: "muted" });
      liveProgressRef.current = null;
      setLiveProgress(null);
    }

    logQueueRef.current.push({ text: cleaned, tone });

    // Update progress markers immediately
    const pct = progressPercent(cleaned);
    if (pct !== null) {
      setProgress((p) => Math.max(p, pct));
    }
  }, [setLiveProgressWithRef]);

  const parseClients = () => parseTenantCodeList(form.clients);

  const buildEnvItems = (targets: string[]) => (
    (form.envCompany || "").trim() || (form.envTenant || "").trim()
      ? [
        {
          company: (form.envCompany || "").trim() || targets[0],
          tenant: (form.envTenant || "").trim() || targets[0],
          tenant_codes: targets,
          sector: (form.envSector || "").trim() || "",
          info: (form.envInfo || "").trim() || "",
          llm_rules: (form.envLlmRules || "").trim() || "",
          taxonomy_dbs: (form.envTaxonomyDbs || "").trim() || "",
          ade_cf: form.manualCf.trim(),
          ade_password: form.manualPassword.trim(),
          ade_pin: form.manualPin.trim(),
          ade_work_profile: normalizedProfilo,
          ade_target_cf_piva: targets[0] || "",
          ade_studio_cf_piva: (form.manualCfPivaStudio || "").trim(),
          ade_date_from: form.dal,
          ade_date_to: form.al || "",
          ade_invoice_mode: form.mode,
          ade_incaricante: (form.incaricante || "").trim(),
        },
      ]
      : targets.map((target) => ({
        company: target,
        tenant: target,
        tenant_codes: [target],
        sector: (form.envSector || "").trim() || "",
        info: (form.envInfo || "").trim() || "",
        llm_rules: (form.envLlmRules || "").trim() || "",
        taxonomy_dbs: (form.envTaxonomyDbs || "").trim() || "",
        ade_cf: form.manualCf.trim(),
        ade_password: form.manualPassword.trim(),
        ade_pin: form.manualPin.trim(),
        ade_work_profile: normalizedProfilo,
        ade_target_cf_piva: target,
        ade_studio_cf_piva: (form.manualCfPivaStudio || "").trim(),
        ade_date_from: form.dal,
        ade_date_to: form.al || "",
        ade_invoice_mode: form.mode,
        ade_incaricante: (form.incaricante || "").trim(),
      }))
  );

  const ensureSelectedEnvFile = useCallback(async (targetTenant?: string): Promise<string | null> => {
    if (form.selectedEnvFile) return form.selectedEnvFile;

    const normalizedTargetTenant = (targetTenant || "").trim();
    const parsedClients = parseClients();
    const fallbackTarget =
      normalizedTargetTenant ||
      (form.envTenant || "").trim() ||
      (form.envCompany || "").trim() ||
      parsedClients[0];
    const envTargets = parsedClients.length ? parsedClients : [fallbackTarget].filter(Boolean);

    if (!envTargets.length) return null;

    const envRes = await api.post<EnvCreateResponse>("/env/create-or-update", {
      env_items: buildEnvItems(envTargets),
    });
    const createdEnv =
      (envRes.data.env_files || []).find(
        (item) => item.tenant === normalizedTargetTenant || item.company === normalizedTargetTenant,
      ) ||
      (envRes.data.env_files || [])[0];

    const refreshedEnvFiles = await reloadEnvFiles();
    const selectedEnv =
      refreshedEnvFiles.find((item) => item.env_file === createdEnv?.env_file) ||
      createdEnv;

    if (!selectedEnv?.env_file) return null;
    applySelectedEnv(selectedEnv);
    return selectedEnv.env_file;
  }, [
    applySelectedEnv,
    form.al,
    form.dal,
    form.envCompany,
    form.envInfo,
    form.envLlmRules,
    form.envSector,
    form.envTaxonomyDbs,
    form.envTenant,
    form.incaricante,
    form.manualCf,
    form.manualCfPivaStudio,
    form.manualPassword,
    form.manualPin,
    form.mode,
    form.selectedEnvFile,
    normalizedProfilo,
    reloadEnvFiles,
  ]);

  const downloadBody = useMemo<DownloadRequestBody>(() => {
    const clients = parseClients();

    return {
      dal: form.dal,
      al: form.al ? form.al : null,
      mode: form.mode,
      extra_args: [
        "--manual-cf",
        form.manualCf.trim(),
        "--manual-password",
        form.manualPassword.trim(),
        "--manual-pin",
        form.manualPin.trim(),
        "--manual-profilo",
        normalizedProfilo,
        ...(form.manualCfPivaStudio?.trim()
          ? ["--manual-cf-piva-studio", form.manualCfPivaStudio.trim()]
          : []),
      ],
      clients,
      incaricante:
        normalizedProfilo === "incDiretto" || normalizedProfilo === "incDelega"
          ? (form.incaricante || "").trim() || null
          : null,
      env_company: (form.envCompany || "").trim() || null,
      env_tenant: (form.envTenant || "").trim() || null,
    };
  }, [
    form.al,
    form.clients,
    form.dal,
    form.envCompany,
    form.envTenant,
    form.incaricante,
    form.manualCf,
    form.manualCfPivaStudio,
    form.manualPassword,
    form.manualPin,
    form.mode,
    normalizedProfilo,
  ]);

  const historicalTenantOptions = useMemo(() => {
    const fromClients = parseClients();
    if (fromClients.length) return fromClients;
    const selected = existingEnvFiles.find((item) => item.env_file === form.selectedEnvFile);
    if (selected?.tenant_codes?.length) return selected.tenant_codes;
    if (selected?.tenant) return [selected.tenant];
    return [];
  }, [existingEnvFiles, form.selectedEnvFile, form.clients]);

  const validate = () => {
    const incaricanteVal = (form.incaricante || "").trim();
    const manualCfVal = (form.manualCf || "").trim().toUpperCase();
    const clientCodes = parseClients();
    const envTenant = (form.envTenant || "").trim();
    if (!form.dal) {
      setLogs((prev) => [...prev, { text: "❌ Inserisci la data iniziale", tone: "error" }]);
      return false;
    }
    if (!clientCodes.length) {
      setLogs((prev) => [...prev, { text: "❌ Inserisci almeno un CF o una P.IVA tenant", tone: "error" }]);
      return false;
    }
    if (envTenant && !isValidTenantCode(envTenant)) {
      setLogs((prev) => [...prev, { text: "❌ Il campo Tenant deve contenere un codice cliente valido (CF/P.IVA)", tone: "error" }]);
      return false;
    }
    if (normalizedProfilo === "incDelega" && !incaricanteVal) {
      setLogs((prev) => [...prev, { text: "❌ Inserisci l'incaricante per incDelega", tone: "error" }]);
      return false;
    }
    if (form.al && form.dal && new Date(form.al) < new Date(form.dal)) {
      setLogs((prev) => [...prev, { text: "❌ La data finale non può precedere quella iniziale", tone: "error" }]);
      return false;
    }
    if (
      normalizedProfilo === "incDiretto" &&
      clientCodes.length &&
      clientCodes.some((value) => value.toUpperCase() !== manualCfVal) &&
      !incaricanteVal
    ) {
      setLogs((prev) => [
        ...prev,
        { text: "❌ Con incarico diretto su altro soggetto devi valorizzare 'Incaricante' con il CF/P.IVA dell'utenza di lavoro da selezionare su ADE.", tone: "error" },
      ]);
      return false;
    }
    if (!form.manualCf || !form.manualPassword || !form.manualPin) {
      setLogs((prev) => [...prev, { text: "❌ Compila tutte le credenziali ADE", tone: "error" }]);
      return false;
    }
    return true;
  };

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      try {
        setCommandPreviewLoading(true);
        const res = await api.post<RunPreviewResponse>("/run-preview", downloadBody);
        setCommandPreview(res.data.commands || []);
        setCommandPreviewMissing(res.data.missing || []);
      } catch {
        setCommandPreview([]);
        setCommandPreviewMissing([]);
      } finally {
        setCommandPreviewLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [downloadBody]);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLogs([{ text: "Avvio processo...", tone: "info" }]);
    setLiveProgress(null);
    setProgress(8);
    setIsRunning(true);
    setStatus("running");

    const controller = new AbortController();
    downloadControllerRef.current = controller;

    const base = (api.defaults.baseURL || "").replace(/\/$/, "");

    let hadError = false;
    let completed = false;

    try {
      const envTargets = downloadBody.clients;
      const envItems = buildEnvItems(envTargets);
      pushLog(`Preparo env file per ${envTargets.length} tenant...`, "info");
      const envRes = await api.post<EnvCreateResponse>("/env/create-or-update", {
        env_items: envItems,
      });
      for (const item of envRes.data.env_files || []) {
        pushLog(`Env creato: ${item.env_file} -> ${item.duckdb_path}`, "info");
      }
      const preferredEnv =
        (envRes.data.env_files || []).find((item) => item.tenant === ((form.envTenant || "").trim() || envTargets[0])) ||
        (envRes.data.env_files || [])[0];
      if (preferredEnv) {
        applySelectedEnv(preferredEnv);
      }
      await reloadEnvFiles();

      const res = await fetch(`${base}/run-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(downloadBody),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText || "Errore avvio job"}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("Streaming non supportato dal browser.");
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;
          const match = chunk.match(/^event: (.+)\ndata: (.+)$/s);
          if (!match) continue;
          const [, eventType, dataStr] = match;
          // Protect against malformed JSON: a parse error would abort the
          // entire stream reading loop, so skip the event and keep going.
          let payload: Record<string, unknown>;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            continue;
          }

          if (eventType === "log" || eventType === "info" || eventType === "warn") {
            const message = String(payload.message ?? "");
            const lower = message.toLowerCase();
            let tone: LogEntry["tone"] = "info";
            const hasErrorWord = /\b(error|errore|failed|fallito)\b/.test(lower);
            const hasWarnWord = /\b(warn|warning|attenzione|avviso)\b/.test(lower);
            const hasSuccessWord = /\b(success|completat[ao])\b/.test(lower) || message.includes("✅");
            if (payload.type === "northflank_ok") {
              tone = "success";
            } else if (payload.type === "northflank_error") {
              tone = "warning";
            } else if (payload.type === "northflank_call") {
              tone = "muted";
            } else if (payload.type === "error" || (hasErrorWord && !lower.includes("errori=0"))) {
              tone = "error";
            } else if (payload.type === "warn" || payload.type === "warning" || hasWarnWord) {
              tone = "warning";
            } else if (payload.type === "success" || hasSuccessWord) {
              tone = "success";
            }
            pushLog(message, tone);
            if (hasSuccessWord || lower.includes("estrazione completata")) {
              setProgress((p) => Math.max(p, 100));
              setStatus("success");
            }
          } else if (eventType === "success") {
            pushLog(String(payload.message ?? ""), "success");
            setProgress((p) => Math.max(p, 60));
          } else if (eventType === "error") {
            pushLog(String(payload.message ?? ""), "error");
            setStatus("error");
            hadError = true;
          } else if (eventType === "complete") {
            pushLog("✅ Processo completato con successo!", "success");
            setProgress(100);
            setStatus("success");
            completed = true;
          }
        }
      }
      // Flush the TextDecoder in case the final chunk contained a partial
      // multi-byte sequence that was buffered internally.
      const decoderRemainder = decoder.decode();
      if (decoderRemainder) {
        buffer += decoderRemainder;
        // Re-process any SSE events that were split across the last two reads.
        const finalChunks = buffer.split("\n\n");
        buffer = finalChunks.pop() || "";
        for (const chunk of finalChunks) {
          if (!chunk.trim()) continue;
          const match = chunk.match(/^event: (.+)\ndata: (.+)$/s);
          if (!match) continue;
          const [, eventType, dataStr] = match;
          let payload: Record<string, unknown>;
          try { payload = JSON.parse(dataStr); } catch { continue; }
          if (eventType === "complete") {
            pushLog("✅ Processo completato con successo!", "success");
            setProgress(100);
            setStatus("success");
            completed = true;
          } else if (eventType === "success") {
            pushLog(String(payload.message ?? ""), "success");
            setProgress((p) => Math.max(p, 60));
          } else if (eventType === "error") {
            pushLog(String(payload.message ?? ""), "error");
            setStatus("error");
            hadError = true;
          } else if (eventType === "log" || eventType === "info" || eventType === "warn") {
            pushLog(String(payload.message ?? ""), "info");
          }
        }
      }
      if (!hadError && !completed) {
        // Il backend ha chiuso lo stream senza evento esplicito: consideralo completato.
        setProgress((p) => Math.max(p, 100));
        setStatus("success");
        pushLog("Stream terminato dal backend.", "info");
      }
    } catch (err: any) {
      if (err?.name === "AbortError") {
        return;
      }
      const msg = err?.message || "Errore di connessione";
      pushLog(`❌ ${msg}`, "error");
      setStatus("error");
    } finally {
      // Persist the last in-flight progress line to the log before clearing it.
      // Without this the final tqdm/progress frame disappears without a trace.
      if (liveProgressRef.current) {
        logQueueRef.current.push({ text: liveProgressRef.current, tone: "muted" });
        liveProgressRef.current = null;
      }
      // Force-flush any remaining queued entries *before* setting isRunning=false.
      // The 150ms batch timer may not have fired since the last events were pushed
      // (e.g. the bronze-trigger notice and the ✅ complete message), and the
      // useEffect([isRunning]) flush runs only *after* the next React commit —
      // too late to guarantee visibility. Calling setLogs directly here closes
      // that gap and ensures every entry is always rendered.
      if (logQueueRef.current.length > 0) {
        const finalBatch = [...logQueueRef.current];
        logQueueRef.current = [];
        setLogs(prev => {
          const next = [...prev, ...finalBatch];
          return next.length > 1000 ? next.slice(-1000) : next;
        });
      }
      setIsRunning(false);
      setLiveProgress(null);
      downloadControllerRef.current = null;
      setTimeout(() => setProgress(0), 800);
      void loadAsyncJobs();
    }
  };

  const handleAbort = () => {
    downloadControllerRef.current?.abort();
    setIsRunning(false);
    pushLog("⚠️ Download interrotto manualmente", "warning");
    setStatus("error");
  };

  const handleHistoricalImport = async () => {
    if (isRunning) return;
    const targetTenant = form.importTenantCode.trim();
    if (!targetTenant) {
      setLogs((prev) => [...prev, { text: "❌ Seleziona il tenant target per l'import storico", tone: "error" }]);
      return;
    }
    if (!isValidTenantCode(targetTenant)) {
      setLogs((prev) => [...prev, { text: "❌ Il tenant target deve contenere un codice cliente valido (CF/P.IVA)", tone: "error" }]);
      return;
    }
    if (form.importSourcePreset && !form.importRemoteClientFolder.trim()) {
      setLogs((prev) => [...prev, { text: "❌ Inserisci la cartella cliente remota", tone: "error" }]);
      return;
    }
    if (!form.importSourcePreset && !form.importRemotePath.trim()) {
      setLogs((prev) => [...prev, { text: "❌ Inserisci il percorso remoto delle fatture", tone: "error" }]);
      return;
    }

    let envFile = form.selectedEnvFile;
    if (!envFile) {
      try {
        envFile = await ensureSelectedEnvFile(targetTenant);
      } catch (err: any) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Impossibile creare o selezionare l'env file";
        setLogs((prev) => [...prev, { text: `❌ ${message}`, tone: "error" }]);
        return;
      }
    }
    if (!envFile) {
      setLogs((prev) => [...prev, { text: "❌ Seleziona un env file prima dell'import storico", tone: "error" }]);
      return;
    }

    setLogs([{ text: "Avvio import storico...", tone: "info" }]);
    setLiveProgress(null);
    setProgress(10);
    setIsRunning(true);
    setStatus("running");

    const controller = new AbortController();
    downloadControllerRef.current = controller;
    const base = (api.defaults.baseURL || "").replace(/\/$/, "");
    let northflankTriggerFailed = false;

    try {
      const body: ImportHistoryRequestBody = {
        env_file: envFile,
        tenant_code: targetTenant,
        direction: form.importSourcePreset ? null : form.importDirection,
        remote_path: form.importSourcePreset ? null : form.importRemotePath.trim(),
        remote_metadata_path: form.importSourcePreset ? null : (form.importRemoteMetadataPath.trim() || null),
        source_preset: form.importSourcePreset || null,
        remote_client_folder: form.importSourcePreset ? form.importRemoteClientFolder.trim() : null,
      };

      const res = await fetch(`${base}/import-history-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errText || "Errore avvio import"}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("Streaming non supportato dal browser.");
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;
          const match = chunk.match(/^event: (.+)\ndata: (.+)$/s);
          if (!match) continue;
          const [, eventType, dataStr] = match;
          let payload: Record<string, unknown>;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            continue;
          }
          const message = String(payload.message ?? "");
          if (eventType === "log" || eventType === "info" || eventType === "warn") {
            let tone: LogEntry["tone"] = "info";
            if (payload.type === "northflank_ok") tone = "success";
            else if (payload.type === "northflank_error") {
              tone = "warning";
              northflankTriggerFailed = true;
            }
            else if (payload.type === "warn" || payload.type === "warning") tone = "warning";
            pushLog(message, tone);
          } else if (eventType === "error") {
            pushLog(message, "error");
            setStatus("error");
          } else if (eventType === "complete") {
            pushLog(
              northflankTriggerFailed
                ? "⚠️ Import storico completato, ma il trigger XML Northflank e' fallito."
                : "✅ Import storico completato!",
              northflankTriggerFailed ? "warning" : "success",
            );
            setProgress(100);
            setStatus(northflankTriggerFailed ? "error" : "success");
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        pushLog(`❌ ${err?.message || "Errore di connessione"}`, "error");
        setStatus("error");
      }
    } finally {
      if (liveProgressRef.current) {
        logQueueRef.current.push({ text: liveProgressRef.current, tone: "muted" });
        liveProgressRef.current = null;
      }
      if (logQueueRef.current.length > 0) {
        const finalBatch = [...logQueueRef.current];
        logQueueRef.current = [];
        setLogs((prev) => {
          const next = [...prev, ...finalBatch];
          return next.length > 1000 ? next.slice(-1000) : next;
        });
      }
      setIsRunning(false);
      setLiveProgress(null);
      downloadControllerRef.current = null;
      setTimeout(() => setProgress(0), 800);
      void loadAsyncJobs();
    }
  };

  const handleLegacySyncTrigger = async () => {
    if (isRunning) return;
    const targetTenant = form.importTenantCode.trim() || form.envTenant.trim();
    if (!targetTenant) {
      setLogs((prev) => [...prev, { text: "❌ Seleziona il tenant target per il sync legacy", tone: "error" }]);
      return;
    }
    if (!isValidTenantCode(targetTenant)) {
      setLogs((prev) => [...prev, { text: "❌ Il tenant target deve contenere un codice cliente valido (CF/P.IVA)", tone: "error" }]);
      return;
    }

    let envFile = form.selectedEnvFile;
    if (!envFile) {
      try {
        envFile = await ensureSelectedEnvFile(targetTenant);
      } catch (err: any) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Impossibile creare o selezionare l'env file";
        setLogs((prev) => [...prev, { text: `❌ ${message}`, tone: "error" }]);
        return;
      }
    }
    if (!envFile) {
      setLogs((prev) => [...prev, { text: "❌ Seleziona un env file prima del sync legacy", tone: "error" }]);
      return;
    }

    setLogs([{ text: "Avvio trigger sync vecchio PC...", tone: "info" }]);
    setLiveProgress(null);
    setProgress(15);
    setIsRunning(true);
    setStatus("running");

    try {
      const body: LegacySyncTriggerRequestBody = {
        env_file: envFile,
        tenant_code: targetTenant,
      };
      pushLog(`→ POST Northflank legacy sync (tenant=${targetTenant})...`, "muted");
      const res = await api.post<LegacySyncTriggerResponse>("/trigger-legacy-sync", body);
      setLegacySyncRetryAt(null);
      pushLog(String(res.data.message || "Trigger legacy sync inviato"), "success");
      void loadAsyncJobs();
      setProgress(100);
      setStatus("success");
    } catch (err: any) {
      const retryAt = err?.response?.data?.retry_at;
      const retryAfterSeconds = err?.response?.data?.retry_after_seconds;
      if (err?.response?.status === 429) {
        if (typeof retryAt === "string" && retryAt) {
          setLegacySyncRetryAt(retryAt);
        } else if (typeof retryAfterSeconds === "number" && retryAfterSeconds > 0) {
          setLegacySyncRetryAt(new Date(Date.now() + retryAfterSeconds * 1000).toISOString());
        }
      }
      const message = extractApiErrorDetail(err, "Errore trigger sync vecchio PC");
      pushLog(`❌ ${message}`, "error");
      if (err?.response?.status === 429) {
        const countdown = formatCountdown(
          typeof retryAfterSeconds === "number" && retryAfterSeconds > 0
            ? Math.ceil(retryAfterSeconds)
            : secondsUntil(retryAt, Date.now()),
        );
        if (countdown) {
          pushLog(`↺ Sync vecchio PC temporaneamente bloccato. Riprova tra ${countdown}.`, "warning");
        }
      }
      setStatus("error");
    } finally {
      if (logQueueRef.current.length > 0) {
        const finalBatch = [...logQueueRef.current];
        logQueueRef.current = [];
        setLogs((prev) => {
          const next = [...prev, ...finalBatch];
          return next.length > 1000 ? next.slice(-1000) : next;
        });
      }
      setIsRunning(false);
      setLiveProgress(null);
      downloadControllerRef.current = null;
      setTimeout(() => setProgress(0), 800);
    }
  };

  const handleSelectAsyncJob = useCallback((job: AsyncJob) => {
    setSelectedAsyncJob(job);
    setAsyncJobModalOpen(true);
    setAsyncJobLogs([]);
    void fetchAsyncJobLogs(job);
  }, [fetchAsyncJobLogs]);

  const handleRefreshAsyncJobFromNorthflank = useCallback(() => {
    if (!selectedAsyncJob || selectedAsyncJob.source !== "northflank") return;
    void fetchAsyncJobLogs(selectedAsyncJob, { localOnly: false, remoteRefresh: true });
  }, [fetchAsyncJobLogs, selectedAsyncJob]);

  const normalizeFileEntries = useCallback((entries: FileEntry[]): FileEntry[] => (
    entries.map((entry) => ({
      ...entry,
      children: entry.children ? normalizeFileEntries(entry.children) : [],
      childrenLoaded: !!entry.children?.length,
    }))
  ), []);

  const replaceTreeChildren = useCallback((entries: FileEntry[], targetPath: string, children: FileEntry[]): FileEntry[] => (
    entries.map((entry) => {
      if (entry.path === targetPath) {
        return { ...entry, children, childrenLoaded: true };
      }
      if (entry.children?.length) {
        return { ...entry, children: replaceTreeChildren(entry.children, targetPath, children) };
      }
      return entry;
    })
  ), []);

  const findTreeEntry = useCallback((entries: FileEntry[], targetPath: string): FileEntry | null => {
    for (const entry of entries) {
      if (entry.path === targetPath) return entry;
      if (entry.children?.length) {
        const found = findTreeEntry(entry.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const fetchFiles = async (path = "") => {
    setFileLoading(true);
    try {
      const res = await api.get("/files", { params: { path, max_depth: 1 } });
      setFileEntries(normalizeFileEntries(res.data.entries || []));
      setFileBase(res.data.base || "/app/data");
      setCurrentPath(res.data.path || path);
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Impossibile leggere i file", tone: "error" });
      setFileEntries([]);
    } finally {
      setFileLoading(false);
    }
  };

  const fetchTreeChildren = useCallback(async (path: string) => {
    setFileLoading(true);
    try {
      const res = await api.get("/files", { params: { path, max_depth: 1 } });
      const children = normalizeFileEntries(res.data.entries || []);
      setFileEntries((prev) => replaceTreeChildren(prev, path, children));
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Impossibile espandere la cartella", tone: "error" });
    } finally {
      setFileLoading(false);
    }
  }, [normalizeFileEntries, replaceTreeChildren, pushToast]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadPathFile = async (path: string, filename: string) => {
    try {
      const res = await api.get("/files/download", {
        params: { path },
        responseType: "blob",
      });
      downloadBlob(res.data, filename);
      pushToast({ id: Date.now(), message: `Scaricato ${filename}`, tone: "info" });
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Download fallito", tone: "error" });
    }
  };

  const handleDownloadFile = async (entry: FileEntry) => {
    // Se è un DB, apri il browser DB e lascia il download esplicito dal modal
    if (entry.name.toLowerCase().endsWith(".duckdb")) {
      setDbModalOpen(true);
      setDbSelected("");
      setDbColumns([]);
      setDbTableRows([]);
      setDbPath(entry.path);
      await fetchDbTables(entry.path);
      return;
    }
    await downloadPathFile(entry.path, entry.name);
  };

  const navigateInto = (entry: FileEntry) => {
    const next = entry.path;
    fetchFiles(next);
  };

  const navigateUp = () => {
    if (!currentPath) {
      fetchFiles("");
      return;
    }
    const parts = currentPath.split("/").filter(Boolean);
    parts.pop();
    fetchFiles(parts.join("/"));
  };

  const toggleTreePath = useCallback(async (path: string) => {
    const current = findTreeEntry(fileEntries, path);
    if (!current) return;

    if (expandedTreePaths[path] === true) {
      setExpandedTreePaths((prev) => ({ ...prev, [path]: false }));
      return;
    }

    if (current.is_dir && !current.childrenLoaded) {
      await fetchTreeChildren(path);
    }
    setExpandedTreePaths((prev) => ({ ...prev, [path]: true }));
  }, [expandedTreePaths, fileEntries, findTreeEntry, fetchTreeChildren, setExpandedTreePaths]);

  const handleDeleteFile = async (entry: FileEntry) => {
    if (!confirm(`Sei sicuro di voler eliminare ${entry.is_dir ? "la cartella" : "il file"} "${entry.name}"?`)) {
      return;
    }
    try {
      await api.delete("/files", { params: { path: entry.path } });
      pushToast({ id: Date.now(), message: `Eliminato ${entry.name}`, tone: "success" });
      fetchFiles(currentPath);
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Eliminazione fallita", tone: "error" });
    }
  };

  const fetchDbTables = async (path?: string) => {
    const targetPath = path || dbPath;
    setDbLoading(true);
    setDbError(null);
    try {
      const res = await api.get("/db/tables", { params: targetPath ? { path: targetPath } : undefined });
      setDbTables(res.data.tables || []);
    } catch (err: any) {
      setDbError(err?.response?.data?.detail || "Impossibile leggere le tabelle");
      setDbTables([]);
    } finally {
      setDbLoading(false);
    }
  };

  const fetchDbTable = async (name: string) => {
    setDbLoading(true);
    setDbError(null);
    try {
      const params: Record<string, string | number> = { name, limit: 100 };
      if (dbPath) params.path = dbPath;
      const res = await api.get("/db/table", { params });
      setDbColumns(res.data.columns || []);
      setDbTableRows(res.data.rows || []);
      setDbSelected(name);
    } catch (err: any) {
      setDbError(err?.response?.data?.detail || "Impossibile leggere la tabella");
      setDbColumns([]);
      setDbTableRows([]);
    } finally {
      setDbLoading(false);
    }
  };

  const handleDownloadDbFile = async () => {
    if (!dbPath) return;
    await downloadPathFile(dbPath, dbPath.split("/").pop() || "database.duckdb");
  };

  const handleDownloadDbTable = async () => {
    if (!dbSelected) return;
    setDbDownloadLoading(true);
    try {
      const params: Record<string, string> = { name: dbSelected };
      if (dbPath) params.path = dbPath;
      const res = await api.get("/db/table/download", {
        params,
        responseType: "blob",
      });
      const safeName = `${dbSelected.replace(/\./g, "_")}.csv`;
      downloadBlob(res.data, safeName);
      pushToast({ id: Date.now(), message: `Scaricata tabella ${dbSelected}`, tone: "info" });
    } catch (err: any) {
      pushToast({ id: Date.now(), message: err?.response?.data?.detail || "Download tabella fallito", tone: "error" });
    } finally {
      setDbDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (isFileModalOpen) {
      fetchFiles(currentPath);
    }
  }, [isFileModalOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-10 bg-slate-950/70 backdrop-blur-lg border-b border-white/5">
        <div className="w-full px-6 sm:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-brand-500/20 border border-brand-400/50 flex items-center justify-center text-brand-200">
              <Zap size={20} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">ADE Downloader</p>
              <h1 className="font-semibold text-lg">Control Room</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative group/nav">
              <button className="btn-ghost px-4 py-2 flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 transition">
                {currentView === "downloader" && <Download size={18} />}
                {currentView === "volume" && <Folder size={18} />}
                {currentView === "users" && <Users size={18} />}
                <span className="text-sm font-medium">
                  {currentView === "downloader" && "Download Fatture"}
                  {currentView === "volume" && "Esplora Volume"}
                  {currentView === "users" && "Gestione Utenti"}
                </span>
                <Loader2 size={14} className="opacity-40 group-hover/nav:rotate-180 transition-transform duration-500" />
              </button>
              <div className="absolute top-full right-0 mt-2 w-64 bg-slate-950 border border-white/10 rounded-2xl shadow-2xl py-2 opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all duration-200 z-50">
                <button
                  className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition ${currentView === "downloader" ? "bg-brand-500/10 text-brand-300" : "hover:bg-white/5"}`}
                  onClick={() => setCurrentView("downloader")}
                >
                  <Download size={18} />
                  <div>
                    <div className="font-semibold">Download Fatture</div>
                    <div className="text-[10px] opacity-60">Scarica XML da AdE</div>
                  </div>
                </button>
                {isAdmin && (
                  <button
                    className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition ${currentView === "users" ? "bg-brand-500/10 text-brand-300" : "hover:bg-white/5"}`}
                    onClick={() => setCurrentView("users")}
                  >
                    <Users size={18} />
                    <div>
                      <div className="font-semibold">Gestione Utenti</div>
                      <div className="text-[10px] opacity-60">Crea e modifica accessi</div>
                    </div>
                  </button>
                )}
                <button
                  className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-white/5 transition"
                  onClick={() => {
                    setIsFileModalOpen(true);
                    fetchFiles("");
                  }}
                >
                  <Folder size={18} />
                  <div>
                    <div className="font-semibold">Esplora Volume</div>
                    <div className="text-[10px] opacity-60">Gestione file locali</div>
                  </div>
                </button>
                <div className="h-px bg-white/5 mx-2 my-1" />
                {authEnabled && (
                  <button
                    className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-white/5 transition"
                    onClick={logout}
                  >
                    <LogOut size={18} />
                    <div>
                      <div className="font-semibold">Logout</div>
                      <div className="text-[10px] opacity-60">Torna al login</div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">
              <ShieldCheck size={16} className="text-emerald-300" />
              <span className="font-semibold">{sessionLabel}</span>
            </div>

            {currentView === "downloader" && (
              <div className="flex items-center gap-3 ml-2">
                <button
                  type="button"
                  className="btn relative z-[60] pointer-events-auto bg-brand-500 hover:bg-brand-400 text-white shadow-none hover:shadow-none"
                  onClick={(e) => {
                    if (isRunning) {
                      e.preventDefault();
                      handleAbort();
                      return;
                    }
                    downloadFormRef.current?.requestSubmit();
                  }}
                >
                  {isRunning ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                  {isRunning ? "In esecuzione..." : "Esegui download"}
                </button>
              </div>
            )}

          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-6 sm:px-8 py-6 space-y-5">
        {currentView === "downloader" ? (
          <div className="panel-grid items-stretch">
            <form
              id="download-form"
              ref={downloadFormRef}
              className="space-y-4 flex flex-col min-h-[calc(100vh-180px)] min-w-0"
              onSubmit={handleStart}
            >
              <div className="flex-1 space-y-4 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                <TenantEnvPanel
                  form={form}
                  updateField={updateField}
                  existingEnvFiles={existingEnvFiles}
                  onSelectExistingEnv={handleSelectExistingEnv}
                />
                <CredentialsPanel form={form} updateField={updateField} />
                <ProfiloPanel normalizedProfilo={normalizedProfilo} updateField={updateField} />
                <ClientsPanel
                  clients={form.clients}
                  incaricante={form.incaricante || ""}
                  studioCfPiva={form.manualCfPivaStudio || ""}
                  updateField={updateField}
                  required={normalizedProfilo !== "incDiretto"}
                  showStudio={normalizedProfilo === "delegaDiretta"}
                  showIncaricante={normalizedProfilo === "incDelega" || normalizedProfilo === "incDiretto"}
                  incaricanteRequired={
                    normalizedProfilo === "incDelega" ||
                    (normalizedProfilo === "incDiretto" &&
                      parseClients().some((value) => value.toUpperCase() !== (form.manualCf || "").trim().toUpperCase()))
                  }
                />
                <HistoricalImportPanel
                  form={form}
                  updateField={updateField}
                  tenantOptions={historicalTenantOptions}
                  onStart={handleHistoricalImport}
                  onTriggerLegacySync={handleLegacySyncTrigger}
                  onApplyPreset={applyHistoricalImportPreset}
                  disabled={isRunning}
                  legacySyncDisabled={legacySyncDisabled}
                  legacySyncHint={legacySyncHint}
                />
                <div className="glass rounded-xl border border-white/10 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-100">Comando equivalente</div>
                      <div className="text-xs text-slate-400 mt-1">
                        Preview calcolata dal backend sui parametri correnti.
                      </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                      {commandPreviewLoading ? "..." : `${commandPreview.length} run`}
                    </div>
                  </div>
                  {commandPreviewLoading ? (
                    <div className="text-sm text-slate-400">Aggiorno la preview...</div>
                  ) : commandPreview.length ? (
                    <div className="space-y-3">
                      {commandPreview.slice(0, 3).map((item) => (
                        <div key={item.target} className="space-y-1">
                          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
                            Target {item.target}
                          </div>
                          <pre className="rounded-xl bg-slate-950/70 border border-white/5 p-3 text-xs text-slate-200 overflow-x-auto whitespace-pre-wrap break-all">
                            <code>{item.command}</code>
                          </pre>
                        </div>
                      ))}
                      {commandPreview.length > 3 && (
                        <div className="text-xs text-slate-400">
                          Altri {commandPreview.length - 3} comandi verranno generati con la stessa struttura.
                        </div>
                      )}
                    </div>
                  ) : commandPreviewMissing.length ? (
                    <div className="text-sm text-slate-400">
                      Completa questi campi per vedere la preview: <span className="text-slate-200">{commandPreviewMissing.join(", ")}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">
                      Compila il form per vedere il comando equivalente.
                    </div>
                  )}
                </div>
              </div>
            </form>

            <div className="flex flex-col h-full min-h-[calc(100vh-180px)] min-w-0 gap-4">
              <LogViewer
                entries={logs}
                title="ADE Downloader Console"
                isStreaming={isRunning}
                progressLine={liveProgress || undefined}
              />
              <AsyncJobQueue
                jobs={asyncJobs}
                loading={asyncJobsLoading}
                selectedJobId={selectedAsyncJob?.id || null}
                onSelectJob={handleSelectAsyncJob}
              />
            </div>
          </div>
        ) : currentView === "users" ? (
          isAdmin ? (
            <UsersAdminPanel pushToast={pushToast} />
          ) : (
            <div className="flex flex-col items-center justify-center h-[60vh] text-slate-400 gap-4">
              <ShieldCheck size={48} className="opacity-20" />
              <p>Solo gli admin possono gestire gli utenti.</p>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-[60vh] text-slate-400 gap-4">
            <Folder size={48} className="opacity-20" />
            <p>Seleziona una funzione dal menu in alto.</p>
          </div>
        )}
      </main>
      {isFileModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-slate-400">
                <FolderOpen size={16} />
                Volume locale
              </div>
              <button className="btn-ghost px-3 py-1.5" onClick={() => setIsFileModalOpen(false)}>
                Chiudi
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
              <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
                <div>
                  Base: {fileBase || "/app/data"}
                  {currentPath ? ` / ${currentPath}` : ""}
                </div>
              </div>
              {fileLoading && (
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <Loader className="animate-spin" size={16} /> Caricamento...
                </div>
              )}
              {!fileLoading && fileEntries.length === 0 && (
                <div className="text-sm text-slate-300">Nessun file trovato.</div>
              )}
              {!fileLoading && fileEntries.length > 0 && (
                <div className="rounded-xl border border-white/10 bg-slate-950/60 p-2">
                  {fileEntries.map((entry) => (
                    <FileTreeNode
                      key={entry.path}
                      entry={entry}
                      busy={fileLoading}
                      expandedPaths={expandedTreePaths}
                      onToggleDir={toggleTreePath}
                      onOpenDir={navigateInto}
                      onDownload={handleDownloadFile}
                      onDelete={handleDeleteFile}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {dbModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-slate-400">
                <Database size={16} />
                {dbPath ? dbPath.split("/").pop() : "database.duckdb"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="btn-ghost px-3 py-1.5"
                  onClick={handleDownloadDbFile}
                  disabled={!dbPath}
                >
                  <DownloadIcon size={14} />
                  Scarica DB
                </button>
                <button className="btn-ghost px-3 py-1.5" onClick={() => setDbModalOpen(false)}>
                  Chiudi
                </button>
              </div>
            </div>
            <div className="flex-1 flex overflow-hidden">
              <div className="w-64 border-r border-white/10 p-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                <div className="text-xs text-slate-400 mb-2">Tabelle</div>
                {dbLoading && (
                  <div className="flex items-center gap-2 text-slate-300 text-sm">
                    <Loader className="animate-spin" size={16} /> Caricamento...
                  </div>
                )}
                {dbError && <div className="text-xs text-rose-300">{dbError}</div>}
                {!dbLoading && dbTables.map((t) => (
                  <button
                    key={t.name}
                    className={`w-full text-left px-3 py-2 rounded-xl ${dbSelected === t.name ? "bg-white/10" : "bg-white/5"} hover:bg-white/15 text-sm flex items-center justify-between gap-2`}
                    onClick={() => fetchDbTable(t.name)}
                    title={t.name}
                  >
                    <span className="truncate max-w-[150px]">{t.name}</span>
                    {typeof t.rows === "number" && <span className="text-xs text-slate-400 shrink-0">{t.rows}</span>}
                  </button>
                ))}
              </div>
              <div className="flex-1 p-4 overflow-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                {dbSelected ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-300">
                        Tabella: <span className="font-semibold text-white">{dbSelected}</span> (prime 100 righe)
                      </div>
                      <button
                        className="btn-ghost px-3 py-1.5"
                        onClick={handleDownloadDbTable}
                        disabled={!dbSelected || dbDownloadLoading}
                      >
                        {dbDownloadLoading ? <Loader2 className="animate-spin" size={14} /> : <DownloadIcon size={14} />}
                        Scarica CSV
                      </button>
                    </div>
                    <div className="overflow-auto border border-white/10 rounded-xl scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                      <table className="min-w-full text-xs text-left">
                        <thead className="bg-white/5">
                          <tr>
                            {dbColumns.map((col) => (
                              <th key={col} className="px-3 py-2 text-slate-300 max-w-[160px] truncate" title={col}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dbTableRows.map((row, idx) => (
                            <tr key={idx} className="border-t border-white/5">
                              {row.map((cell, cidx) => (
                                <td key={cidx} className="px-3 py-2 text-slate-100 max-w-[200px] truncate" title={cell === null ? "NULL" : String(cell)}>
                                  {cell === null ? <span className="text-slate-500">NULL</span> : String(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {dbLoading && (
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Loader className="animate-spin" size={16} /> Caricamento tabella...
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-300">Seleziona una tabella per visualizzare i dati.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {asyncJobModalOpen && selectedAsyncJob && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-slate-400">
                  {ASYNC_STAGE_LABELS[selectedAsyncJob.stage] || selectedAsyncJob.stage}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Tenant {selectedAsyncJob.tenant_codes?.[0] || selectedAsyncJob.tenant || selectedAsyncJob.company || "n/d"}
                  {selectedAsyncJob.run_id ? ` · run ${selectedAsyncJob.run_id}` : ""}
                  {asyncJobsRefreshedAt ? ` · sync ${formatDateTime(asyncJobsRefreshedAt)}` : ""}
                </div>
                {selectedAsyncJobRemoteHint && (
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-500/10 px-3 py-1 text-[11px] font-medium text-amber-200">
                    <AlertTriangle size={14} />
                    {selectedAsyncJobRemoteHint}
                  </div>
                )}
                {isNorthflankLogAccessWarning(selectedAsyncJob) && (
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-500/10 px-3 py-1 text-[11px] font-medium text-amber-200">
                    <AlertTriangle size={14} />
                    {getAsyncJobDisplayMessage(selectedAsyncJob)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="btn-ghost px-3 py-1.5"
                  onClick={handleRefreshAsyncJobFromNorthflank}
                  disabled={
                    asyncJobLogsLoading
                    || selectedAsyncJob.source !== "northflank"
                    || (selectedAsyncJobRetrySeconds !== null && selectedAsyncJobRetrySeconds > 0)
                  }
                >
                  <CloudDownload size={14} />
                  Aggiorna da Northflank
                </button>
                <div className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                  selectedAsyncJob.status === "success"
                    ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                    : selectedAsyncJob.status === "failed"
                      ? "border-rose-400/30 bg-rose-500/10 text-rose-300"
                      : "border-sky-400/30 bg-sky-500/10 text-sky-300"
                }`}>
                  {selectedAsyncJob.status === "success" ? "OK" : selectedAsyncJob.status === "failed" ? "KO" : selectedAsyncJob.status}
                </div>
                <button className="btn-ghost px-3 py-1.5" onClick={() => setAsyncJobModalOpen(false)}>
                  Chiudi
                </button>
              </div>
            </div>
            <div className="p-4 space-y-3 overflow-hidden flex-1 flex flex-col">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-slate-400">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="uppercase tracking-[0.14em] text-slate-500">Sorgente</div>
                  <div className="text-slate-200 mt-1">{selectedAsyncJob.source}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="uppercase tracking-[0.14em] text-slate-500">Inizio</div>
                  <div className="text-slate-200 mt-1">{formatDateTime(selectedAsyncJob.started_at)}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="uppercase tracking-[0.14em] text-slate-500">Ultimo update</div>
                  <div className="text-slate-200 mt-1">{formatDateTime(selectedAsyncJob.updated_at)}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <div className="uppercase tracking-[0.14em] text-slate-500">Stato log</div>
                  <div className="text-slate-200 mt-1">
                    {asyncJobLogsLoading ? "aggiorno..." : asyncJobLogsLive ? "live" : "snapshot"}
                    {selectedAsyncJob.remote_state === "stale" ? " · cached" : ""}
                    {selectedAsyncJob.remote_state === "rate_limited" ? " · 429 cooldown" : ""}
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1">
                <LogViewer
                  entries={asyncJobLogs.length ? asyncJobLogs : [{ text: "In attesa dei log del job...", tone: "muted" }]}
                  title={`${ASYNC_STAGE_LABELS[selectedAsyncJob.stage] || selectedAsyncJob.stage} Logs`}
                  isStreaming={asyncJobLogsLive}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToastStack({ toasts, remove }: { toasts: Toast[]; remove: (id: number) => void }) {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 w-72">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`rounded-xl px-4 py-3 border border-white/10 ${toast.tone === "success"
            ? "bg-emerald-950 text-emerald-100"
            : toast.tone === "error"
              ? "bg-rose-950 text-rose-100"
              : toast.tone === "warning"
                ? "bg-amber-950 text-amber-100"
                : "bg-slate-900 text-slate-100"
            }`}
        >
          {toast.message}
          <button
            className="float-right text-xs text-white/70 hover:text-white"
            onClick={() => remove(toast.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function AppContent() {
  const { user, loading, authEnabled } = useAuth();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const pushToast = (toast: Toast) => {
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => removeToast(toast.id), 4500);
  };

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    document.title = "ADE Invoice Downloader";
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-300" size={32} />
      </div>
    );
  }

  return (
    <>
      <ToastStack toasts={toasts} remove={removeToast} />
      {!authEnabled || user ? <Dashboard pushToast={pushToast} /> : <LoginView pushToast={pushToast} />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
